import JsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import xlsx from 'xlsx';
import logo from '@/common/logo';

class ReportesExportService {
  constructor() {
    this.formatter = new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    });
  }

  procesarDatosInforme(creditos, listaParametros, selectedParams) {
    const columnsParams = [];
    
    if (selectedParams.length > 0) {
      const seleccionados = listaParametros.filter((items) =>
        selectedParams.includes(items.id)
      );
      seleccionados.map((items) =>
        columnsParams.push({ title: items.nombre, dataKey: items.clave })
      );
    }

    const datos = creditos.map((legajos) => ({
      legajo: legajos.id,
      operatoria: legajos.lineaCredito_id.nombre,
      estadoCredito: legajos.estadoCredito.nombre,
      cuit_beneficiario: legajos.cliente.dni
        ? legajos.cliente.dni
        : legajos.cliente.cuit,
      nombre_beneficiario:
        legajos.cliente.apellido !== '' && legajos.cliente.nombre !== ''
          ? `${legajos.cliente.apellido} ${legajos.cliente.nombre}`
          : legajos.cliente.razonSocial,
      fecha_desembolso: legajos.fecha_desembolso,
      cbu: legajos.cliente.cbu,
      email: legajos.cliente.domicilioElectronico,
      celular: legajos.cliente.celular,
      estado: legajos.estado.estado,
      estadoManual: legajos.estado.estadosmanuales_id
        ? legajos.estado.estadosmanuales_id.definicion
        : '',
      tipoMora: legajos.estado.tipomora_id
        ? legajos.estado.tipomora_id.definicion
        : '',
      observacion: legajos.estado.observacion,
      diasMora: legajos.estado.diasatraso,
      saldoCapital: legajos.estado.saldocapital || 0,
      parametros: legajos.parametros.map((parametro) => ({
        id: parametro.id,
        valor: parametro.valor,
        clave: parametro.tipo_parametro_id.clave,
        nombre: parametro.tipo_parametro_id.nombre,
        pesos: parametro.tipo_parametro_id.options === 'float',
      })),
    }));

    return { datos, columnsParams };
  }

  formatearDatosParaInforme(datos) {
    return datos.map((legajos) => {
      const { parametros, saldoCapital, ...rest } = legajos;
      const saldoFormateado = parseFloat(saldoCapital);
      const parametrosObj = parametros.reduce((acc, param) => {
        const valorFormateado = param.pesos
          ? parseFloat(param.valor)
          : param.valor;
        acc[param.clave] = valorFormateado;
        return acc;
      }, {});
      return {
        ...rest,
        ...parametrosObj,
        saldoFormateado,
      };
    });
  }

  formatearDatosParaPDF(datos) {
    return datos.map((legajos) => {
      const { parametros, saldoCapital, ...rest } = legajos;
      const saldoFormateado = this.formatter.format(saldoCapital);
      const parametrosObj = parametros.reduce((acc, param) => {
        const valorFormateado = param.pesos
          ? this.formatter.format(param.valor)
          : param.valor;
        acc[param.clave] = valorFormateado;
        return acc;
      }, {});
      return {
        ...rest,
        ...parametrosObj,
        saldoFormateado,
      };
    });
  }

  obtenerColumnasPredeterminadas() {
    return [
      { title: 'Legajo', dataKey: 'legajo' },
      { title: 'DEUDOR', dataKey: 'nombre_beneficiario' },
      { title: 'CUIL/CUIT/DNI', dataKey: 'cuit_beneficiario' },
      { title: 'CBU', dataKey: 'cbu' },
      { title: 'Email', dataKey: 'email' },
      { title: 'Operatoria', dataKey: 'operatoria' },
      { title: 'Estado Crédito', dataKey: 'estadoCredito' },
      { title: 'Fecha Desembolso', dataKey: 'fecha_desembolso' },
      { title: 'Celular', dataKey: 'celular' },
      { title: 'Estado', dataKey: 'estado' },
      { title: 'Estado Manual', dataKey: 'estadoManual' },
      { title: 'Tipo Mora', dataKey: 'tipoMora' },
      { title: 'Observacion', dataKey: 'observacion' },
      { title: 'Dias Mora', dataKey: 'diasMora' },
      { title: 'Saldo Capital', dataKey: 'saldoFormateado' },
    ];
  }

  obtenerColumnasInfoMoraFinanciamiento() {
    return [
      { title: 'Legajo', dataKey: 'legajo' },
      { title: 'Fideicomiso', dataKey: 'fideicomiso' },
      { title: 'Operatoria / Línea', dataKey: 'operatoria_linea' },
      { title: 'Apellido y Nombre / Razón Social', dataKey: 'nombre_razon_social' },
      { title: 'CUIT', dataKey: 'cuit' },
      { title: 'Persona', dataKey: 'persona' },
      { title: 'Fecha de Ingreso', dataKey: 'fecha_ingreso' },
      { title: 'Estado', dataKey: 'estado' },
      { title: 'Monto', dataKey: 'monto' },
      { title: 'Monto ANR', dataKey: 'monto_anr' },
      { title: 'Fecha de Otorgamiento', dataKey: 'fecha_otorgamiento' },
      { title: 'Cantidad Cuotas', dataKey: 'cantidad_cuotas' },
      { title: 'Saldo Capital', dataKey: 'saldo_capital' },
      { title: 'Monto Cobrado', dataKey: 'monto_cobrado' },
      { title: 'Cuotas en Mora', dataKey: 'cantidad_cuotas_mora' },
      { title: 'Capital Vencido', dataKey: 'capital_vencido' },
      { title: 'Días de Mora', dataKey: 'dias_mora' },
      { title: 'Estado Morosidad', dataKey: 'estado_morosidad' },
      { title: 'Estado Manual', dataKey: 'estado_manual' },
    ];
  }

  generarPDF(informesPDF, columnsParams, filtroOperatoria, fechaInicio, fechaFinal, tipo = 0, tipoReporte = null) {
    let columnasPredeterminadas;
    
    if (tipoReporte === 'Info Mora (Financiamiento)') {
      columnasPredeterminadas = this.obtenerColumnasInfoMoraFinanciamiento();
    } else {
      columnasPredeterminadas = this.obtenerColumnasPredeterminadas();
    }
    
    const doc = new JsPDF('l', 'mm', [297, 410]);
    const columnsHands = columnasPredeterminadas.concat(columnsParams);

    const maxColumnWidths = columnsHands.map((column) => {
      const maxWidth = informesPDF.reduce((max, row) => {
        const cellValue = String(row[column.dataKey]);
        const width = doc.getStringUnitWidth(cellValue);
        return Math.max(max, width);
      }, 0);
      return maxWidth + 16;
    });

    const height = 50;
    const imgData = logo;
    doc.addImage(imgData, 'PNG', 10, 5, 48, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(`${filtroOperatoria}`, 15, 30);
    doc.setFontSize(13);
    doc.text(`Fecha: ${moment().format('DD/MM/YYYY')}`, 150, 30);
    doc.setFontSize(15);
    doc.text(
      `Periodo desde: ${
        fechaInicio
          ? moment(fechaInicio, 'YYYY-MM-DD').format('DD/MM/YYYY')
          : '-'
      } Hasta: ${moment(
        fechaFinal ? fechaFinal : new Date(),
        'YYYY-MM-DD'
      ).format('DD/MM/YYYY')}`,
      15,
      40
    );
    
    doc.autoTable(columnsHands, informesPDF, {
      startY: height,
      columnWidth: maxColumnWidths.length > 0 ? maxColumnWidths : 'wrap',
      columnStyles: columnsHands.map((column, index) => ({
        cellWidth:
          maxColumnWidths[index] - (column.dataKey === 'legajo' ? 1 : 0),
      })),
      styles: {
        fontSize: 8,
        cellPadding: 1,
        lineHeight: 0.4,
        halign: 'center',
        valign: 'middle',
      },
      theme: 'grid',
      margin: { left: 10, right: 10, top: 10, bottom: 10 },
      headStyles: {
        fillColor: [0, 0, 255],
        textColor: [255, 255, 255],
      },
    });

    if (tipo === 0) {
      window.open(doc.output('bloburl'), '_blank');
    }
    if (tipo === 1) {
      doc.save(
        `${filtroOperatoria}-${moment().format('DD/MM/YYYY')}.pdf`      );
    }

    return doc;
  }

  generarExcel(informes, columnsParams, filtroOperatoria, tipoReporte = null) {
    let columnasPredeterminadas;
    
    if (tipoReporte === 'Info Mora (Financiamiento)') {
      columnasPredeterminadas = this.obtenerColumnasInfoMoraFinanciamiento();
    } else {
      columnasPredeterminadas = this.obtenerColumnasPredeterminadas();
    }
    
    const columnsHands = columnasPredeterminadas.concat(columnsParams);
    
    let informesExcel = [];

    if (informes.length !== 0) {
      informesExcel = informes.map((informe) => {
        const nuevoObjeto = {};
        columnsHands.forEach((columna) => {
          nuevoObjeto[columna.title] = informe[columna.dataKey];
        });
        return nuevoObjeto;
      });
    } else {
      informesExcel = [
        {
          'Nº Legajo': '',
          Operatoria: '',
          EstadoCrédito: '',
          'CUIL/CUIT/DNI': '',
          Beneficiario: '',
          'Fecha Desembolso': '',
          CBU: '',
          Celular: '',
          Estado: '',
          'Estado Manual': '',
          'Tipo Mora': '',
          Observacion: '',
          'Dias Mora': '',
          'Saldo Capital': '',
        },
      ];
    }

    const ws = xlsx.utils.json_to_sheet(informesExcel);
    const wb = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(wb, ws, 'People');
    xlsx.writeFile(
      wb,
      `${filtroOperatoria}-${moment().format('DD/MM/YYYY')}.xlsx`
    );
  }
}

export default new ReportesExportService();